import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { 
  FileText, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Eye,
  AlertCircle,
  Shield,
  User,
  Building,
  FileDown
} from 'lucide-react';
import { exportListPDF } from '@/utils/pdfExport';

export default function Customers() {
  const { token } = useContext(AuthContext);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showDocumentDialog, setShowDocumentDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, pending, verified, rejected

  useEffect(() => {
    fetchDocuments();
    markCustomersSeen();
  }, []);

  const markCustomersSeen = async () => {
    try {
      await axios.put(`${API}/customers/mark-seen`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Failed to mark customers as seen');
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/admin/proof-documents/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data.documents || []);
    } catch (error) {
      toast.error('Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (customerId, customerName) => {
    try {
      await axios.post(`${API}/admin/proof-documents/${customerId}/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Document verified for ${customerName}`);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify document');
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    try {
      await axios.post(
        `${API}/admin/proof-documents/${selectedCustomer.customer_id}/reject`,
        { reason: rejectionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Document rejected for ${selectedCustomer.customer_name}`);
      setShowRejectDialog(false);
      setRejectionReason('');
      setSelectedCustomer(null);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject document');
    }
  };

  const getStatusBadge = (status, hasDocument) => {
    if (!hasDocument) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          <AlertCircle className="w-3 h-3" />
          No Document
        </span>
      );
    }

    const configs = {
      Pending: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, text: 'Verifying' },
      Verified: { color: 'bg-green-100 text-green-700', icon: CheckCircle, text: 'Completed' },
      Rejected: { color: 'bg-red-100 text-red-700', icon: XCircle, text: 'Rejected' }
    };

    const config = configs[status] || configs.Pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.text}
      </span>
    );
  };

  const filteredDocuments = documents.filter(doc => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'no-document') return !doc.proof_document_url;
    return doc.verification_status === filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1);
  });

  const stats = {
    total: documents.length,
    pending: documents.filter(d => d.verification_status === 'Pending' && d.proof_document_url).length,
    verified: documents.filter(d => d.verification_status === 'Verified').length,
    rejected: documents.filter(d => d.verification_status === 'Rejected').length,
    noDocument: documents.filter(d => !d.proof_document_url).length
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p>Loading...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const handleExportPDF = () => {
    const rows = documents.map((doc, i) => [
      i + 1,
      doc.customer_name || '-',
      doc.email || '-',
      doc.business_name || '-',
    ]);
    exportListPDF({
      title: 'Customers',
      headers: ['#', 'Name', 'Email', 'Business'],
      rows,
      token, apiUrl: API,
    });
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Customer Verification Portal</h1>
            <p className="text-slate-600 mt-2">Review and verify customer proof of role documents for all registered companies</p>
          </div>
          <Button variant="outline" onClick={handleExportPDF} data-testid="customers-export-pdf">
            <FileDown className="w-4 h-4 mr-2" /> PDF
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card className="p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilterStatus('all')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Total Customers</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              </div>
              <FileText className="w-8 h-8 text-slate-400" />
            </div>
          </Card>

          <Card className="p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilterStatus('pending')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-400" />
            </div>
          </Card>

          <Card className="p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilterStatus('verified')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Verified</p>
                <p className="text-2xl font-bold text-green-600">{stats.verified}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
          </Card>

          <Card className="p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilterStatus('rejected')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Rejected</p>
                <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
          </Card>

          <Card className="p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilterStatus('no-document')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">No Document</p>
                <p className="text-2xl font-bold text-gray-600">{stats.noDocument}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-gray-400" />
            </div>
          </Card>
        </div>

        {/* Documents List */}
        <Card className="p-6">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-slate-900">
              {filterStatus === 'all' ? 'All Customers' : 
               filterStatus === 'no-document' ? 'Customers Without Documents' :
               `${filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1)} Documents`}
            </h2>
          </div>

          {filteredDocuments.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">No documents found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredDocuments.map(doc => (
                <Card key={doc.customer_id} className="p-4 border-slate-200 hover:border-emerald-300 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        doc.is_company_admin ? 'bg-emerald-100' : 'bg-blue-100'
                      }`}>
                        {doc.is_company_admin ? (
                          <Shield className="w-6 h-6 text-emerald-600" />
                        ) : (
                          <User className="w-6 h-6 text-blue-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-900">{doc.customer_name}</h3>
                          {doc.is_company_admin && (
                            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Admin</span>
                          )}
                          {getStatusBadge(doc.verification_status, doc.proof_document_url)}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-600">
                          <span>{doc.email}</span>
                          <span className="flex items-center gap-1">
                            <Building className="w-3 h-3" />
                            {doc.business_name}
                          </span>
                          {doc.role && <span>Role: {doc.role}</span>}
                        </div>
                        {doc.rejection_reason && (
                          <p className="text-sm text-red-600 mt-2 flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 mt-0.5" />
                            <span><strong>Rejection Reason:</strong> {doc.rejection_reason}</span>
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      {doc.proof_document_url && (
                        <Button
                          onClick={() => {
                            setSelectedCustomer(doc);
                            setShowDocumentDialog(true);
                          }}
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-2"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </Button>
                      )}
                      {doc.proof_document_url && doc.verification_status === 'Pending' && (
                        <>
                          <Button
                            onClick={() => handleVerify(doc.customer_id, doc.customer_name)}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Verify
                          </Button>
                          <Button
                            onClick={() => {
                              setSelectedCustomer(doc);
                              setShowRejectDialog(true);
                            }}
                            size="sm"
                            variant="destructive"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* View Document Dialog */}
      <Dialog open={showDocumentDialog} onOpenChange={setShowDocumentDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Proof Document - {selectedCustomer?.customer_name}</DialogTitle>
          </DialogHeader>
          <div className="h-[70vh]">
            <iframe
              src={selectedCustomer?.proof_document_url}
              className="w-full h-full border rounded"
              title="Proof Document"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Provide a reason for rejecting {selectedCustomer?.customer_name}'s proof document:
            </p>
            <div>
              <Label>Rejection Reason *</Label>
              <Input
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="e.g., Document is unclear, incorrect format..."
                className="mt-2"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowRejectDialog(false);
                  setRejectionReason('');
                  setSelectedCustomer(null);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleReject}
                variant="destructive"
                className="flex-1"
              >
                Reject Document
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
